import { z } from 'zod';

import { LocalDateSchema } from './my-work.dto';
import { MobileDefaultTabSchema, WeekStartSchema } from './users';

/**
 * My personal work preferences and saved views (docs/release-1/17-V2-BUILD-PLAN.md
 * §5 W4-0; migration 0116).
 *
 * PRIVATE, like the rest of My Work (§2.2): stored in self-only tables (FORCE
 * RLS + a RESTRICTIVE guard) that no team owner, superadmin, agent, report or
 * SYSTEM job can read. Served only by the me-prefs routes under `/api/me/*`
 * (browser JWT or a PAT with `me.plan:*`; agent PATs and impersonation
 * refused — P6). Nothing here is ever shown to anyone else, and no team
 * surface derives anything from it (P11).
 *
 * Endpoints:
 *  - GET   /api/me/work-prefs             → WorkPrefs
 *  - PATCH /api/me/work-prefs             PatchWorkPrefs → WorkPrefs
 *  - GET   /api/me/saved-views?surface=   → SavedView[] (by surface, then position)
 *  - POST  /api/me/saved-views            CreateSavedView → SavedView (201)
 *  - PATCH /api/me/saved-views/:id        UpdateSavedView → SavedView
 *  - DELETE /api/me/saved-views/:id       → 204
 *  - PATCH /api/me/saved-views/order      ReorderSavedViews → SavedView[] (that surface)
 *  - POST  /api/me/recents/projects       RecordRecentProject → 204
 *                                         (404 `project_not_found` for a project I can't open)
 * Mutations take an `Idempotency-Key`. An unknown or foreign view id → 404
 * `saved_view_not_found` (never 403); a 51st view on a surface → 409
 * `saved_views_limit`.
 *
 * Wave 6 (§7 W6-0; migration 0119) adds the Personal-tier pieces below:
 * ritual prefs on WorkPrefs, the W6 push payload, the private "My plan" ICS
 * feed (`/api/me/calendar/plan-feed`) and the own timesheet CSV export.
 */

/** Week board capacity until I set my own: 6 h. */
export const DEFAULT_DAILY_CAPACITY_MINUTES = 360;
/** Most recent projects remembered for the Projects tab / switcher. */
export const RECENT_PROJECTS_MAX = 20;
/** Saved views per surface. */
export const SAVED_VIEWS_MAX_PER_SURFACE = 50;
/** Size cap of a saved view's `query`, in characters of `JSON.stringify` (the DB allows more). */
export const SAVED_VIEW_QUERY_MAX_CHARS = 4096;

// ── Onboarding (server state, survives devices) ─────────────────────────────

/** The one onboarding question: Just me · With my team · I was invited. */
export const OnboardingIntentSchema = z.enum(['solo', 'team', 'invited']);
export type OnboardingIntent = z.infer<typeof OnboardingIntentSchema>;

/** Step ids are owned by the onboarding flow (W4-C), e.g. `capture_3`, `invite_link`. */
export const OnboardingStepIdSchema = z.string().regex(/^[a-z0-9_.-]{1,40}$/);

export const OnboardingStateSchema = z.object({
  /** null until the question is answered (`invited` is set automatically from an invite token). */
  intent: OnboardingIntentSchema.nullable(),
  /** Steps done so far, in the order they were done; no duplicates. */
  completedSteps: z.array(OnboardingStepIdSchema).max(32),
  /** ISO time I closed onboarding for good; null while it may still show. */
  dismissedAt: z.string().datetime({ offset: true }).nullable(),
});
export type OnboardingState = z.infer<typeof OnboardingStateSchema>;

export const DEFAULT_ONBOARDING_STATE: OnboardingState = {
  intent: null,
  completedSteps: [],
  dismissedAt: null,
};

// ── Rituals (W6, Personal tier; migration 0119) ─────────────────────────────

/** A local wall-clock time `HH:MM` (24 h) in my profile time zone. */
export const HhMmSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM (24 h)');
export type HhMm = z.infer<typeof HhMmSchema>;
/** Day of week, JS / Postgres numbering: 0 = Sunday … 6 = Saturday. */
export const WeekdaySchema = z.number().int().min(0).max(6);

/** Rituals are off until I turn them on (never on day 1). */
export const DEFAULT_RITUAL_PREFS = {
  morningPlanAt: null,
  wrapUpAt: null,
  weeklyReviewDay: null,
  hideTitlesInPush: false,
  planFeedEnabled: false,
} as const satisfies Record<string, HhMm | number | boolean | null>;

/** What the Rituals settings offer when a ritual is switched on. */
export const SUGGESTED_RITUALS = { morningPlanAt: '08:30', wrapUpAt: '18:00', weeklyReviewDay: 5 } as const;

/**
 * Ritual push windows (the scheduler, `core.due_rituals` in 0120):
 *  - morning  "Plan your day"  once per local day, from `morningPlanAt` for 2 h;
 *  - wrap_up  "Wrap up"        once per local day, from `wrapUpAt` for 2 h;
 *  - weekly_review             once per local day on `weeklyReviewDay`, from
 *                              `wrapUpAt` (17:00 when wrap-up is off) for 2 h.
 *                              That day it is the ONE evening push: the wrap-up
 *                              is folded into it ("N tasks left for today …").
 * A window never crosses local midnight; a missed window is skipped, never late.
 */
export const RITUAL_WINDOW_MINUTES = 120;
export const WEEKLY_REVIEW_FALLBACK_AT = '17:00';
export const RitualKindSchema = z.enum(['morning', 'wrap_up', 'weekly_review']);
export type RitualKind = z.infer<typeof RitualKindSchema>;

/**
 * The Web Push payload of every W6 push (plan reminders + rituals) — what the
 * server sends (`push.service.ts`, W6-A) and the service worker reads (W6-D).
 * Sent to ME only (P2/P7). With `hideTitlesInPush` the title / body carry no
 * task title ("Reminder" / "A task in your plan").
 */
export const PlanPushKindSchema = z.enum([
  'plan_reminder',
  'ritual_morning',
  'ritual_wrap_up',
  'ritual_weekly_review',
]);
export type PlanPushKind = z.infer<typeof PlanPushKindSchema>;
export const PlanPushPayloadSchema = z.object({
  kind: PlanPushKindSchema,
  title: z.string().max(120),
  body: z.string().max(240),
  /** Relative app path the notification opens (`/my/work?…`, `?task=KEY`). */
  url: z.string().startsWith('/'),
  /** Collapses repeats in the OS tray: `plan-reminder:<taskId>`, `ritual:<kind>:<localDate>`. */
  tag: z.string().max(120),
  /** App-icon badge = my `todayRemaining` at send time; null = leave the badge alone. */
  badge: z.number().int().min(0).nullable(),
  /** `plan_reminder` only. */
  taskId: z.string().uuid().nullable(),
});
export type PlanPushPayload = z.infer<typeof PlanPushPayloadSchema>;

/**
 * Where each W6 push opens (`PlanPushPayload.url`). My Work reads `ritual`
 * (W6-D: morning → the "✨ Plan my day" panel when AI is ready, else Today;
 * wrap_up → the Wrap-up flow; weekly_review → the 4-step review) and `task`
 * (`useTaskUrl`: opens that task).
 */
export const RITUAL_PUSH_URLS: Record<RitualKind, string> = {
  morning: '/my/work?view=today&ritual=morning',
  wrap_up: '/my/work?view=today&ritual=wrap_up',
  weekly_review: '/my/work?view=week&ritual=weekly_review',
};
export function planReminderPushUrl(taskKey: string): string {
  return `/my/work?view=today&task=${encodeURIComponent(taskKey)}`;
}

// ── Work prefs ──────────────────────────────────────────────────────────────

/**
 * GET /api/me/work-prefs — always complete (defaults until I change one).
 * `weekStart`, `timezone` and `mobileDefaultTab` are the same values as on
 * `/api/me` (they live on my user row); the rest are My Work prefs.
 *
 * The W6 fields (rituals, `hideTitlesInPush`, `planFeedEnabled`) are always
 * sent by W6 servers; they are optional only so pre-W6 builders and fixtures
 * still compile — read them with `DEFAULT_RITUAL_PREFS` as the fallback.
 * PATCHing a non-null ritual time / day without entitlement → 402
 * `upgrade_required`; `null` (off) and `hideTitlesInPush` are always allowed.
 */
export const WorkPrefsSchema = z.object({
  /** Week board capacity per day, in minutes (0 = don't track capacity). */
  dailyCapacityMinutes: z.number().int().min(0).max(1440),
  weekStart: WeekStartSchema,
  /** IANA time zone (validated server-side). Drives "today" everywhere. */
  timezone: z.string().min(1).max(64),
  /** Mobile landing tab; `auto` = My Work, or Home when Lead+ in a regular team. */
  mobileDefaultTab: MobileDefaultTabSchema,
  /** Show the My Work count badge (sidebar + mobile tab). */
  showMyWorkBadge: z.boolean(),
  /** "Hide my private panels on team screens" — the 🔒 panel starts collapsed in task views. */
  hidePrivatePanels: z.boolean(),
  onboarding: OnboardingStateSchema,
  /**
   * Projects I opened most recently, newest first (≤ 20), written by
   * `POST /api/me/recents/projects`. Ids I can no longer open are dropped on read.
   */
  recentProjectIds: z.array(z.string().uuid()).max(RECENT_PROJECTS_MAX),
  /** Morning "Plan your day" push time; null = off. */
  morningPlanAt: HhMmSchema.nullable().optional(),
  /** Evening "Wrap up" push time; null = off. */
  wrapUpAt: HhMmSchema.nullable().optional(),
  /** Weekly review push day (0 = Sunday … 6 = Saturday); null = off. */
  weeklyReviewDay: WeekdaySchema.nullable().optional(),
  /** Pushes about my plan never show task titles (lock-screen privacy). */
  hideTitlesInPush: z.boolean().optional(),
  /** Read-only here: my private "My plan" ICS feed exists (managed by `/api/me/calendar/plan-feed`). */
  planFeedEnabled: z.boolean().optional(),
  /** Nudges (20261021): "Don't ask me about deadlines" — no deadline nudge after I create a task. */
  nudgeDeadlineOff: z.boolean().optional(),
  /** Nudges (20261021): "Don't ask me to log time" — no time chips when I complete a task. */
  nudgeTimeOff: z.boolean().optional(),
});
export type WorkPrefs = z.infer<typeof WorkPrefsSchema>;

/**
 * PATCH /api/me/work-prefs — any subset; `onboarding` is replaced as a whole.
 * Recents and the plan feed have their own routes.
 */
export const PatchWorkPrefsSchema = WorkPrefsSchema.omit({
  recentProjectIds: true,
  planFeedEnabled: true,
})
  .partial()
  .strict();
export type PatchWorkPrefsInput = z.infer<typeof PatchWorkPrefsSchema>;

/** POST /api/me/recents/projects — "I opened this project" (moves it to the front). */
export const RecordRecentProjectSchema = z.object({ projectId: z.string().uuid() }).strict();
export type RecordRecentProjectInput = z.infer<typeof RecordRecentProjectSchema>;

// ── Saved views ─────────────────────────────────────────────────────────────

/** Where a saved view applies: My Work › All, or a project's task list. */
export const SavedViewSurfaceSchema = z.enum(['my_work_all', 'project_list']);
export type SavedViewSurface = z.infer<typeof SavedViewSurfaceSchema>;

const SavedViewName = z.string().trim().min(1).max(60);

/**
 * The filter set, as the surface's own URL search params (e.g. `{ role,
 * status, team, project }`). Opaque to the server except that team / project
 * ids inside it are re-validated on read and dropped when I can no longer see
 * them. Never shared: there is no "share with team" on a saved view, ever.
 */
const SavedViewQuery = z
  .record(z.unknown())
  .refine((q) => JSON.stringify(q).length <= SAVED_VIEW_QUERY_MAX_CHARS, {
    message: `query must serialize to at most ${SAVED_VIEW_QUERY_MAX_CHARS} characters`,
  });

export const SavedViewSchema = z.object({
  id: z.string().uuid(),
  surface: SavedViewSurfaceSchema,
  name: z.string().min(1).max(60),
  query: z.record(z.unknown()),
  /** 0-based order within the surface. */
  position: z.number().int().min(0),
});
export type SavedView = z.infer<typeof SavedViewSchema>;

export const SavedViewsQuerySchema = z
  .object({ surface: SavedViewSurfaceSchema.optional() })
  .strict();
export type SavedViewsQuery = z.infer<typeof SavedViewsQuerySchema>;

/** POST /api/me/saved-views — appended at the end of its surface. */
export const CreateSavedViewSchema = z
  .object({ surface: SavedViewSurfaceSchema, name: SavedViewName, query: SavedViewQuery })
  .strict();
export type CreateSavedViewInput = z.infer<typeof CreateSavedViewSchema>;

/** PATCH /api/me/saved-views/:id — rename and/or replace the filters. */
export const UpdateSavedViewSchema = z
  .object({ name: SavedViewName.optional(), query: SavedViewQuery.optional() })
  .strict()
  .refine((v) => v.name !== undefined || v.query !== undefined, {
    message: 'Nothing to update',
  });
export type UpdateSavedViewInput = z.infer<typeof UpdateSavedViewSchema>;

/** PATCH /api/me/saved-views/order — the surface's views in their new order (all of them). */
export const ReorderSavedViewsSchema = z
  .object({
    surface: SavedViewSurfaceSchema,
    ids: z.array(z.string().uuid()).min(1).max(SAVED_VIEWS_MAX_PER_SURFACE),
  })
  .strict()
  .refine((v) => new Set(v.ids).size === v.ids.length, {
    message: 'ids must be unique',
    path: ['ids'],
  });
export type ReorderSavedViewsInput = z.infer<typeof ReorderSavedViewsSchema>;

// ── "My plan" ICS feed (W6-C, Personal tier; migration 0119) ────────────────

/**
 * A private, read-only calendar of MY plan (day plans + time blocks), off by
 * default and separate from the deadlines feed (`/api/calendar/me/…`, which
 * never gets plan items — L10).
 *
 *   GET    /api/me/calendar/plan-feed   → PlanFeed (`url` always null: only a hash is stored)
 *   POST   /api/me/calendar/plan-feed   EnablePlanFeed → PlanFeed (201) — creates OR rotates
 *          the secret (the old URL stops working); `url` is shown this once
 *   PATCH  /api/me/calendar/plan-feed   UpdatePlanFeed → PlanFeed — titles ⇄ "Busy", same URL
 *   DELETE /api/me/calendar/plan-feed   → 204 (the URL stops working)
 *   GET    /api/calendar/plan/:token.ics  public (the token IS the credential) → text/calendar
 *
 * Token = `<my user id>.<secret>` (`formatPlanFeedToken`), secret = 32 random
 * bytes base64url; my work prefs store only sha256(secret) hex (migration
 * 0119). The public route reads the hash under `withUserContext(<user id>)`
 * (self-only RLS, no SYSTEM read, no SECURITY DEFINER) and compares in
 * constant time; every failure (bad shape, no feed, wrong secret, entitlement
 * lapsed) is the same 404 `plan_feed_not_found`, so it is never an oracle.
 * `busyOnly` events read "Busy" with no title, key or link.
 * Creating / enabling needs entitlement (402 `upgrade_required`); GET / DELETE never.
 */
export const PlanFeedSchema = z.object({
  enabled: z.boolean(),
  busyOnly: z.boolean(),
  /** Absolute subscription URL — only in the POST response; null everywhere else. */
  url: z.string().url().nullable(),
});
export type PlanFeed = z.infer<typeof PlanFeedSchema>;

export const EnablePlanFeedSchema = z.object({ busyOnly: z.boolean().default(false) }).strict();
export type EnablePlanFeedInput = z.infer<typeof EnablePlanFeedSchema>;
export const UpdatePlanFeedSchema = z.object({ busyOnly: z.boolean() }).strict();
export type UpdatePlanFeedInput = z.infer<typeof UpdatePlanFeedSchema>;

const UUID_PART = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
/** `<uuid>.<43-char base64url secret>`. */
export const PLAN_FEED_TOKEN_RE = new RegExp(`^(${UUID_PART})\\.([A-Za-z0-9_-]{43})$`);

export function formatPlanFeedToken(userId: string, secret: string): string {
  return `${userId.toLowerCase()}.${secret}`;
}

/** `{ userId, secret }`, or null for anything that isn't a well-formed token. */
export function parsePlanFeedToken(token: string): { userId: string; secret: string } | null {
  const m = PLAN_FEED_TOKEN_RE.exec(token);
  return m ? { userId: m[1]!, secret: m[2]! } : null;
}

/** Path of the public feed (prefix it with the API origin). */
export function planFeedPath(token: string): string {
  return `/api/calendar/plan/${token}.ics`;
}

// ── Own timesheet export (W6-C, Personal tier) ──────────────────────────────

/** Longest range one export covers. */
export const TIMESHEET_EXPORT_MAX_DAYS = 366;

/**
 * GET /api/me/time/export.csv?teamId&from&to — MY time entries only, in one
 * team I'm an active member of (my Personal team allowed), `from`..`to`
 * inclusive local dates (my time zone). Never another member's rows, never a
 * plan / overlay column. 402 `upgrade_required` without entitlement; a team
 * I'm not in → 404 `team_not_found`. Columns: `MY_TIMESHEET_CSV_COLUMNS`
 * (RFC 4180, UTF-8, CRLF, header row; `Content-Disposition: attachment`).
 */
export const MyTimesheetExportQuerySchema = z
  .object({ teamId: z.string().uuid(), from: LocalDateSchema, to: LocalDateSchema })
  .strict()
  .refine((v) => v.from <= v.to, { message: 'from must be on or before to', path: ['to'] })
  .refine(
    (v) => (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000 < TIMESHEET_EXPORT_MAX_DAYS,
    { message: `at most ${TIMESHEET_EXPORT_MAX_DAYS} days`, path: ['to'] },
  );
export type MyTimesheetExportQuery = z.infer<typeof MyTimesheetExportQuerySchema>;

export const MY_TIMESHEET_CSV_COLUMNS = [
  'date',
  'started_at',
  'ended_at',
  'minutes',
  'project_key',
  'project_name',
  'task_key',
  'task_title',
  'billable',
  'note',
] as const;
